import * as React from "react";
import useLoadingPresentation, { LoadingPresentation } from "../../../../shared/hooks/useLoadingPresentation";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";

/**
 * The first load of the Screens panel's "Recently Visited" list.
 *
 * Its rows come from a request of their own (`GetRecentlyOpenedScreens`), which lands after the
 * screen list the panel is already drawing — so the section stood open and empty under its header
 * until they arrived. This is the shape the designer's loading rule calls a skeleton: a known shape
 * loading for the first time over the network (docs/designer.md → Motion → Loading).
 *
 * It is a component of its own because `useLoadingPresentation` is a hook and `ScreensWindow` is a
 * class: the gating lives here — nothing before `LOADING_SKELETON_DELAY_MS`, so a fast answer never
 * flashes a placeholder, and once drawn it stays `LOADING_SKELETON_MIN_VISIBLE_MS` — while the rows
 * stay with the class that owns them.
 *
 * The section sits above the screen list, so the placeholder is drawn ONLY at a height this project
 * is known to settle at (`expectedRows`, see `recently-visited-row-memory.ts`). A guessed height is
 * worse than none: when the real rows replace it, every screen row below moves by the difference,
 * and a click already on its way opens the screen that slid under the cursor.
 */
export interface IRecentlyVisitedRegionProps {
  isLoading: boolean;
  /** Whether the section already has rows of its own — with them a reload keeps them, it never re-shimmers. */
  hasRows: boolean;
  /**
   * How many rows this project settled at last time. `undefined` — nothing remembered yet — means
   * the height is unknown, so the section waits instead of guessing one.
   */
  expectedRows: number | undefined;
  children: React.ReactNode;
}

const RecentlyVisitedRegion = ({
  isLoading,
  hasRows,
  expectedRows,
  children
}: IRecentlyVisitedRegionProps): JSX.Element => {
  const presentation: LoadingPresentation = useLoadingPresentation({ isLoading, hasData: hasRows });
  const hasKnownHeight: boolean = expectedRows !== undefined && expectedRows > 0;

  if (presentation === "skeleton" && hasKnownHeight) {
    return (
      <KuikaSkeletonRegion label="Loading recently visited screens" className="kuika-screens__recent-skeleton">
        {/* `comfortable` is the row height of the KuikaMenuItem rows it stands in for, so nothing jumps. */}
        <KuikaSkeletonList rows={expectedRows} density="comfortable" withIcon lines={1} />
      </KuikaSkeletonRegion>
    );
  }

  // Loading, and either the delay has not run out or this project's height is not known yet: draw
  // neither a placeholder nor an empty section — which is what this section did until now.
  if (presentation === "pending" || presentation === "skeleton") {
    return <></>;
  }

  return <>{children}</>;
};

export default RecentlyVisitedRegion;
